import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../components/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { apiError } from '../../lib/api';
import Turnstile, { TURNSTILE_SITE_KEY } from '../../components/Turnstile';

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const captchaRef = React.useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!TURNSTILE_SITE_KEY && !verified) { toast.error('Merci de confirmer que tu n es pas un robot'); return; }
    setLoading(true);
    try {
      // Fresh, single-use captcha token at submit time (see Turnstile.jsx).
      let token = '';
      if (TURNSTILE_SITE_KEY) {
        token = await captchaRef.current?.getToken();
        if (!token) {
          toast.error(captchaRef.current?.scriptBlocked()
            ? 'Le contrôle de sécurité est bloqué : désactive ton bloqueur de publicités sur ce site, puis réessaie.'
            : 'Le contrôle anti-robot a échoué, réessaie.');
          setLoading(false); return;
        }
      }
      await login(email, password, token);
      toast.success('Content de te revoir !');
      nav('/dashboard');
    } catch (err) {
      // Token already consumed by the attempt — refresh for the next try.
      captchaRef.current?.reset();
      toast.error(apiError(err, 'Identifiant ou mot de passe incorrect'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#06060a] grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="relative rounded-3xl border border-white/10 bg-[#0B0B0E]/80 backdrop-blur-xl p-8 shadow-2xl shadow-indigo-500/10">
          <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-400 flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-blue-400 to-red-400" /> Google
          </div>

          <div className="flex flex-col items-center mb-6">
            <Logo size="lg" />
          </div>
          <h1 className="text-3xl font-display font-bold text-white text-center mb-2">Connexion</h1>
          <p className="text-sm text-slate-400 text-center mb-7">Les employés se connectent avec leur e-mail, les partenaires avec leur identifiant</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">E-mail ou identifiant</Label>
              <Input data-testid="login-email-input" type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@domaine.com ou jeandupont" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Mot de passe</Label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11" required />
            </div>

            {TURNSTILE_SITE_KEY ? (
              <Turnstile ref={captchaRef} />
            ) : (
              <div className="rounded-md bg-white border border-slate-200 p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox data-testid="login-captcha-checkbox" checked={verified} onCheckedChange={setVerified} className="border-slate-400" />
                  <span className="text-sm text-slate-700">Confirme que tu n’es pas un robot</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-slate-500">
                  <Shield size={14} className="text-orange-500" /> <span>Captcha</span>
                </div>
              </div>
            )}

            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-white/10 hover:bg-indigo-500 text-white h-11 transition-colors">
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>

            <div className="flex flex-col items-center gap-2 text-sm pt-2">
              <Link to="/auth/forgot-password" className="text-slate-400 hover:text-white">Mot de passe oublié ?</Link>
              <Link data-testid="login-signup-link" to="/auth/register" className="text-indigo-400 hover:text-indigo-300">Pas encore de compte ? S'inscrire</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
